import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["folderId", "folderPath", "fileName"],
  _excluded2 = ["folderId", "folderPath"];
/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable require-await */
/* eslint-disable hubspot-dev/no-async-await */

import { List, Map as ImmutableMap, fromJS } from 'immutable';
import http from 'hub-http/clients/apiClient';
import getApiUrl from '../utils/getApiUrl';
import { ObjectCategory } from '../Constants';
import { FileAccessValues } from '../enums/InternalFileManagerFileAccess';
import { localUrlToFileUrl } from 'file-manager-components/internal/utils';
import { trimFileName } from 'file-manager-components/utils/trimFileName';
const BASE_URI = 'filemanager/api/v2/files';
const BASE_V3_URI = 'filemanager/api/v3/files';

// Fix Firefox download issue: Files without encoding (like .tgz, .tar.gz) get truncated extensions.
// Set content-type to octet-stream and include filename in content-disposition to preserve correct extension.
export const CONTENT_TYPE_OVERRIDE_EXTENSIONS = new Set(['tgz', 'tar.gz', 'tar.bz2', 'tar.xz', 'tar.zst']);
export const buildFileFromAttrs = attrs => {
  attrs.category = ObjectCategory.FILE;
  return fromJS(attrs);
};
const buildFilesPayload = resp => ImmutableMap(Object.assign({}, resp, {
  objects: List(resp.objects.map(buildFileFromAttrs))
}));
export function archiveFile(fileId) {
  const URI = `${BASE_URI}/${fileId}/archive`;
  return http.post(URI, {
    data: {
      archived: true
    }
  }).then(buildFileFromAttrs);
}
export function unarchiveFile(fileId) {
  const URI = `${BASE_URI}/${fileId}/archive`;
  return http.post(URI, {
    data: {
      archived: false
    }
  }).then(buildFileFromAttrs);
}
export async function fetchSingleFile(fileId) {
  return http.get(`${BASE_V3_URI}/${fileId}`).then(buildFileFromAttrs);
}
export async function move(fileId, options) {
  const URI = `${BASE_V3_URI}/${fileId}/move-file`;
  return http.post(URI, {
    data: Object.assign({}, options.name && {
      newName: options.name
    }, options.folder_id !== undefined && {
      newParentFolderId: options.folder_id
    })
  }).then(buildFileFromAttrs);
}
const sanitizeFileName = fileName => fileName.replace(/\./gm, '-');
const COMMON_FILE_UPLOAD_PARAMS = {
  timeout: 0,
  headers: {
    'content-type': false
  }
};
export const uploadFileV3 = (file, uploadedFileAccess, fileUploadOptions = {}, {
  updateProgress,
  xhrCallback,
  httpClient = http
} = {}) => {
  if (!file) {
    throw new Error('`file` arg is required and must be a local File');
  }
  if (!Object.keys(FileAccessValues).includes(uploadedFileAccess)) {
    throw new Error('[FileManagerCore/uploadFile(V3)] Missing or invalid uploadedFileAccess param. Visit https://product.hubteam.com/docs/file-manager-manual/Frontend/index.html for details.');
  }
  const {
      folderId,
      folderPath,
      fileName
    } = fileUploadOptions,
    extraOptions = _objectWithoutPropertiesLoose(fileUploadOptions, _excluded);
  extraOptions.access = uploadedFileAccess;
  let preNormalizedFileName;
  if (fileName) {
    preNormalizedFileName = fileName;
  } else if (file instanceof File && file.name) {
    preNormalizedFileName = file.name;
  }
  const formData = new FormData();
  if (preNormalizedFileName && !fileName) {
    const normalizedFileName = trimFileName(preNormalizedFileName).normalize('NFKC');
    formData.append('file', file, normalizedFileName);
  } else {
    formData.append('file', file);
  }
  if (folderId) {
    formData.append('folderId', folderId.toString());
  }
  if (folderPath) {
    formData.append('folderPath', folderPath);
  }
  if (fileName) {
    const trimmedFileName = trimFileName(fileName);
    formData.append('fileName', fileUploadOptions.preserveExtension ? trimmedFileName : sanitizeFileName(trimmedFileName));
  }
  formData.append('options', JSON.stringify(extraOptions));
  return httpClient.post(`${BASE_V3_URI}/upload`, Object.assign({}, COMMON_FILE_UPLOAD_PARAMS, {
    data: formData,
    withXhr: xhr => {
      xhr.upload.addEventListener('progress', updateProgress, false);
      if (xhrCallback) {
        xhrCallback(xhr);
      }
    }
  })).then(buildFilesPayload);
};
export const uploadFile = uploadFileV3;
export const completeMultiPartUpload = (fileSize, fileName, uploadId, filePartETags, multipartUploadKeyName, fileUploadOptions) => {
  const {
      folderId,
      folderPath
    } = fileUploadOptions,
    extraOptions = _objectWithoutPropertiesLoose(fileUploadOptions, _excluded2);
  return http.post(`${BASE_V3_URI}/multipart-upload/complete`, {
    // This endpoint can take a long time to complete, so we explicitly specify a timeout of 0
    // timeout of 0 means the request will not be automatically terminated by the http client
    timeout: 0,
    data: Object.assign({}, folderId && {
      folderId
    }, folderPath && {
      folderPath
    }, {
      size: fileSize,
      fileName,
      filePartETags,
      uploadId,
      key: multipartUploadKeyName,
      options: extraOptions
    })
  }).then(buildFileFromAttrs);
};
export const completeMultiPartReplace = (fileId, uploadedFileAccess, multipartUploadKeyName, uploadId, filePartETags) => http.post(`${BASE_V3_URI}/multipart-upload/replace`, {
  data: {
    fileId,
    options: {
      access: uploadedFileAccess
    },
    key: multipartUploadKeyName,
    uploadId,
    filePartETags
  }
}).then(buildFileFromAttrs);
export const replaceFile = async (fileId, file, uploadedFileAccess, {
  updateProgress
} = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('options', JSON.stringify({
    access: uploadedFileAccess
  }));
  return http.post(`${BASE_V3_URI}/${fileId}/replace`, Object.assign({}, COMMON_FILE_UPLOAD_PARAMS, {
    data: formData,
    withXhr: xhr => {
      xhr.upload.addEventListener('progress', updateProgress, false);
    }
  })).then(buildFilesPayload);
};
export async function uploadResizedImage(fileId, newWidth) {
  return http.post(`${BASE_URI}/${fileId}/resize-image`, {
    timeout: 0,
    query: {
      width: newWidth
    }
  }).then(buildFileFromAttrs);
}
export async function recalculateImageDimensions(fileId) {
  return http.post(`${BASE_V3_URI}/${fileId}/recalculate-image-dimensions`, {
    timeout: 5000
  }).then(buildFileFromAttrs);
}
export async function exportFiles(emailAddress, filesAndFolders = {}) {
  const uri = `${BASE_URI}/export-all-files`;
  return http.post(uri, {
    data: Object.assign({
      email_address: emailAddress
    }, filesAndFolders)
  }).then(fromJS);
}
const LOCAL_FILE_PATHS = ['local.hsappstatic.net/FileManagerImages'];
export async function downloadFromUrl({
  fileDownloadOptions = {},
  url,
  uploadedFileAccess
}) {
  if (!Object.keys(FileAccessValues).includes(uploadedFileAccess)) {
    throw new Error('[FileManagerCore/downloadFromUrl] Missing or invalid uploadedFileAccess param. Visit https://product.hubteam.com/docs/file-manager-manual/Frontend/index.html for details.');
  }

  /**
   * If we're in development mode, we may be pointing to a locally served file, e.g. from FileManagerImages
   * To prevent a 400 error from the backend trying to reference a local file, we upload the file to the backend instead of downloading from a URL
   * This code will get stripped out by Webpack when we build for QA/Production
   */
  if (process.env.NODE_ENV === 'development') {
    if (LOCAL_FILE_PATHS.some(path => url.includes(path))) {
      const file = await localUrlToFileUrl(url);
      const fileUploadResponse = await uploadFileV3(file, uploadedFileAccess, fileDownloadOptions);
      return fileUploadResponse.getIn(['objects', 0]);
    }
  }
  return http.post(`${BASE_V3_URI}/synchronous-download-from-url`, {
    timeout: 60000,
    data: Object.assign({}, fileDownloadOptions, {
      access: uploadedFileAccess,
      url
    })
  }).then(buildFileFromAttrs);
}

// Note that this will copy with no TTL by default unless specified
export async function copyFile({
  fileId,
  requestBody
}) {
  return http.post(`${BASE_URI}/${fileId}/copy-file`, {
    data: requestBody
  }).then(buildFileFromAttrs);
}
export function remove(fileId) {
  return http.delete(`${BASE_URI}/${fileId}`);
}
export function getDownloadUrl(path, version) {
  return getApiUrl(`${BASE_URI}/download/${path}`, {
    version
  });
}
export async function removeCanvaId(fileId) {
  return http.delete(`${BASE_V3_URI}/${fileId}/canva`).then(buildFileFromAttrs);
}
export async function setCanvaId(fileId, canvaId) {
  return http.put(`${BASE_V3_URI}/${fileId}/canva`, {
    data: {
      canvaId
    }
  }).then(buildFileFromAttrs);
}
const getSignedUrlParams = ({
  expirationSeconds,
  fileName,
  contentType,
  isAttachment
}) => Object.assign({}, expirationSeconds && {
  expirationSeconds
}, contentType && {
  'response-content-type': contentType
}, isAttachment && {
  'response-content-disposition': fileName ? `attachment; filename="${encodeURIComponent(fileName)}"` : 'attachment'
});

/**
 * Makes an HTTP request to get a signed URL for the file
 */
export function fetchSignedUrl(fileId, options = {}) {
  return http.get(`${BASE_URI}/${fileId}/signed-url`, {
    query: getSignedUrlParams(options)
  });
}

// Deprecated: use fetchSignedUrl instead
export function getSignedUrl(fileId, options = {}) {
  return fetchSignedUrl(fileId, options);
}

// Deprecated: use fetchSignedUrl with isAttachment: true
export function getDownloadableSignedUrl(fileId, options = {}) {
  return fetchSignedUrl(fileId, Object.assign({}, options, {
    isAttachment: true
  }));
}

/**
 * Constructs a signed redirect URL for the file (does not make HTTP request)
 * @returns The constructed URL string that will redirect to the file when accessed
 */
export function getSignedRedirectUrl(fileId, options = {}) {
  return getApiUrl(`${BASE_URI}/${fileId}/signed-url-redirect`, getSignedUrlParams(options));
}

// Deprecated: use getSignedRedirectUrl with isAttachment: true
export function getSignedUrlRedirectDownloadUrl(fileId, options = {}) {
  return getSignedRedirectUrl(fileId, Object.assign({}, options, {
    isAttachment: true
  }));
}